<template>
    <!-- Background image container for hero -->
    <div v-if="!keywordError && specificBg" class="d-flex" :class="bgLoaded ? 'specific-hero-bg' : false">
        <img :src="specificBg" @load="() => bgLoaded = true" v-show="bgLoaded">
    </div>

	<div v-if="keywordError" class="container-fluid px-0">
		<NavButtons light-btns></NavButtons>
		<div class="d-md-none d-flex align-items-center justify-content-between small-nav">
			<a v-load-page="pageSettings.previousPage" v-ripple
			   class="navbar-brand fw-bold fs-4 py-0 m-0 rounded-circle">
				<i class="hoverable-icon bi-arrow-left"></i>
			</a>
		</div>
		<div class="container-fluid px-0 text-center">
			<h3>Oops! It seems like this page does not exist.</h3>
			<p>But no worries, we have many more pages and medication reviews to be explored.</p>
			<p>Please check the URL or navigate back to the home page.</p>

			<RouterLink to="/">Go back to the Explore Meds homepage</RouterLink>
		</div>
	</div>
    <div v-else class="container-fluid px-0">
        <!-- Hero section -->
        <div class="row hero-section specific-bg d-flex flex-column">
            <NavButtons :light-btns="!bgLoaded"
                :margin-class="width >= 1650 ? 'mb-xxl' : width >= 1400 ? 'mb-large' : 'mb-tablet'"></NavButtons>

            <!-- Mobile Nav -->
            <div class="d-md-none d-flex align-items-center justify-content-between small-nav mx-0 px-0 mb-large">
                <!-- Back icon -->
                <a v-load-page="pageSettings.previousPage" v-ripple
                    class="navbar-brand fw-bold fs-4 py-0 m-0 rounded-circle">
                    <i class="hoverable-icon bi-arrow-left specific-style"></i>
                </a>
                <!-- Menu toggler -->
                <div v-if="isAdmin" class="dropdown">
                    <i class="hoverable-icon bi-three-dots-vertical specific-style" v-ripple data-bs-toggle="dropdown" aria-expanded="false"></i>
                    <ul class="dropdown-menu dropdown-menu-end">
                        <li><a class="dropdown-item" href="#" @click.prevent="openEditModal">Edit description</a></li>
                    </ul>
                </div>
            </div>

            <div v-if="!loadingMainContent" class="col-12 col-md-6 col-lg-8 col-xl-5 d-flex flex-column justify-content-center"
                :class="{ 'text-white text-shadow': bgLoaded, 'mt-auto mb-3': width < 768 }">
                <!-- Badge -->
                <span
                    class="badge text-bg-primary rounded-pill p-2 fs-6 w-max-content px-3 mb-4 fw-light cursor-pointer"
                    :class="{ 'fs-7': width < 768, 'mb-14': width >= 767 && width < 1400 }"
                    @click="router.push('/'+props.basic)">{{ pageSettings.basePage }}</span>

                <!-- Title -->
	            <div class="d-flex align-items-center">
		            <h2 class="fw-semibold text-start hero-title" v-max-lines="width < 768 ? 3 : 2">
			            {{ pageSettings.title }}
		            </h2>
		            <!-- Menu toggler -->
		            <div v-if="isAdmin" class="dropdown d-none d-md-block ms-2 mb-1">
			            <i class="hoverable-icon bi-three-dots-vertical specific-style" v-ripple data-bs-toggle="dropdown" aria-expanded="false"></i>
			            <ul class="dropdown-menu dropdown-menu-end">
				            <li><a class="dropdown-item" href="#" @click.prevent="openEditModal">Edit description</a></li>
			            </ul>
		            </div>
	            </div>


                <!-- Description -->
                <div class="mt-3" v-if="!loadingMainContent && mainContent">
                    <p class="hero-desc cursor-pointer" v-html="mainContent.about" v-max-lines="3"
                        @click="() => showInfoModal = true"></p>
                </div>
            </div>
	        <div v-else class="modal-body d-flex flex-column py-4 align-items-center justify-content-between">
		        <div class="d-flex flex-column align-items-center gap-2">
			        <div class="spinner-border" role="status">
				        <span class="visually-hidden">Loading...</span>
			        </div>
			        <div class="fs-md">Loading...</div>
		        </div>
	        </div>

            <!-- Stats Swiper -->
            <Swiper v-if="stats.length > 0 && !loadingMainContent" id="statsContainer" class="d-flex gap-3 align-items-center mb-4 in-row-swiper" :class="{
                'mt-auto': width >= 768,
                'mb-35': width >= 768 && width < 1400,
                'mb-36': width >= 1400 && width < 1650,
                'mb-5': width >= 1650
            }" tag="div">
                <template #content>
	                <template v-for="(stat, index) in stats" :key="'stat_' + index">
		                <div v-if="stat.value && stat.name" class="d-flex flex-column flex-shrink-0 justify-content-between gap-3 p-3 rounded-1 bg-white wh-info cursor-pointer"
		                     @click="openStatsModal(stat)" >
			                <p class="m-0 d-flex justify-content-between align-items-center">
				                <span class="fs-4 fw-medium">{{ stat.value }}</span>
				                <i v-if="stat.icon" class="bi inline-icon fs-4 align-items-center" :class="stat.icon"></i>
			                </p>
			                <p class="m-0 text-muted fw-medium ws-initial" v-max-lines="2">{{ stat.name }}</p>
		                </div>
	                </template>
                </template>
            </Swiper>

        </div>

        <!-- Tab headings -->
        <Swiper id="mainTabsContainer" v-show="!loadingMainContent" class="nav nav-tabs has-icon align-items-end border-0 in-page-swiper gap-lg"
            role="tablist" tag="ul">
            <template #content>
                <li class="nav-item align-items-center h-100" role="presentation" v-for="(tab, index) in tabs">
                    <button class="position-relative nav-link h-100 p-0 d-flex flex-column align-items-center gap-1 py-2 fw-medium" :class="index == activeIndex ? 'active' : ''" data-bs-toggle="tab" :data-bs-target="`#tab-${index}-pane`" type="button" role="tab" @click="changeTab(tab.slug)" v-ripple>
                        <i class="fs-3 h-auto w-auto" :class="categories.find(c => c.label.toLowerCase() === tab.name.toLowerCase()).iconClass"></i>
                        <span>{{ tab.name }}</span>
                    </button>
                </li>
            </template>
        </Swiper>

        <!-- Divider -->
        <div class="divider sm" v-show="!loadingMainContent"></div>

        <!-- Tabs content -->
        <div class="tab-content mt-4" v-show="!loadingMainContent">
            <!-- Tab container -->
            <div v-for="(tab, index) in tabs" class="tab-pane" :class="index === activeIndex ? 'show active' : ''" :id="`tab-${index}-pane`" role="tabpanel" :tabindex="index" >

                <!-- Heading -->
                <h2 class="fw-bold text-start">{{ tab.name }}</h2>

                <!-- Filters -->
                <Filters :filter-options="tab.filterOptions" @filters-updated="(value) => tab.selectedFilter = value"
                    :sort="tab.selectedFilter?.sort" :sort_dir="tab.selectedFilter?.dir">
                </Filters>

                <div class="tab-items">
                    <CardsContainer :url="tab.url" :filters="tab.selectedFilter" :parent="tab.parent" :page="'specific'"
                        :specificType="'medication'" />
                </div>
            </div>
        </div>

	    <div v-if="loadingMainContent" class="modal-body d-flex flex-column py-4 align-items-center justify-content-between">
		    <div class="d-flex flex-column align-items-center gap-2">
			    <div class="spinner-border" role="status">
				    <span class="visually-hidden">Loading...</span>
			    </div>
			    <div class="fs-md">Loading...</div>
		    </div>
	    </div>

	    <StatModal @close-modal="handleModalClose('stat')" v-if="showStatModal" :stat="statToLoad" :type="typeForDemographicStat" :id="mainContent.id"></StatModal>

	    <InfoModal @close-modal="handleModalClose('info')" v-if="showInfoModal" :title="pageSettings.title"
	               :description="mainContent.about"></InfoModal>

        <!-- Edit About Modal -->
        <div class="modal fade" id="editAboutModalSpecific" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered popOutModal">
                <div class="modal-content">
                    <div class="modal-body">
                        <textarea class="form-control" rows="6" v-model="editableAbout" placeholder="Enter description..."></textarea>
                    </div>
	                <div class="modal-footer pt-0 border-0">
		                <button class="btn btn-outline-secondary" @click="closeEditModal" :disabled="saving">Cancel</button>
		                <button class="btn btn-dark" @click="saveAbout" :disabled="saving">
			                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
			                Save
		                </button>
	                </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from "vue";
import { useWindowSize } from '@vueuse/core';
import NavButtons from "@/components/NavButtons.vue";
import Swiper from "@/components/Swiper.vue";
import Filters from "@/components/Table/Filters.vue";
import CardsContainer from '@/components/Card/CardsContainer.vue';
import InfoModal from "@/components/GenericModalAndViews/InfoModal.vue";
import tab from "bootstrap/js/dist/tab";
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import { useAppHelpers } from '@/composables/useAppHelpers';
import { categories } from '@/components/nav/navitems';
import { useRoute, useRouter } from "vue-router";

import img1 from '@/assets/specific-1.jpg';
import img2 from '@/assets/specific-2.jpeg';
import img3 from '@/assets/specific-3.jpeg';
import img4 from '@/assets/specific-4.jpeg';
import img5 from '@/assets/specific-5.jpeg';
import img6 from '@/assets/specific-6.jpeg';
import img7 from '@/assets/specific-7.jpeg';
import img8 from '@/assets/specific-8.jpeg';
import img9 from '@/assets/specific-9.jpeg';
import img10 from '@/assets/specific-10.jpeg';
import img11 from '@/assets/specific-11.jpeg';
import img12 from '@/assets/specific-12.jpg';
import '../../assets/scss/specific.scss';
import StatModal from "@/components/GenericModalAndViews/StatModal.vue";
import Modal from "bootstrap/js/dist/modal";
import { useAuthStore } from "@/stores/auth-store";
import { useToast } from "@/composables/useToast";

const props = defineProps({
	basic: String,
    specific: String
});
const router = useRouter();
const route = useRoute();
const helper = useAppHelpers();
const { width } = useWindowSize();

const pageSettings = ref({
	'title': '...',
	'previousPage': '/' + props.basic,
});

const bgLoaded = ref(false);
const showInfoModal = ref(false);
const showStatModal = ref(false);
const specificBg = ref('');

const { displayToast } = useToast();
const auth = useAuthStore();
const isAdmin = computed(() => auth.is_admin);

const editableAbout = ref('');
const saving = ref(false);
const editModalInstance = ref(null);

const openEditModal = () => {
  editableAbout.value = mainContent.value?.about || '';
  const el = document.getElementById('editAboutModalSpecific');
  editModalInstance.value = Modal.getOrCreateInstance(el);
  editModalInstance.value.show();
};

const closeEditModal = () => {
  if (editModalInstance.value) {
    editModalInstance.value.hide();
  }
};

const saveAbout = async () => {
  try {
    saving.value = true;
    const resp = await apiClient.put(`${props.basic}/${props.specific}`, { about: editableAbout.value });
    if (resp.status >= 200 && resp.status < 300) {
      if (mainContent.value) {
        mainContent.value.about = editableAbout.value;
      }
      displayToast('Description has been successfully updated.', 'outline-success');
      closeEditModal();
    }
  } catch (error) {
    await useHttpErrorManager().handleError(error, true);
  } finally {
    saving.value = false;
  }
};

const images = [
    img1,
    img2,
    img3,
    img4,
    img5,
    img6,
    img7,
    img8,
    img9,
    img10,
    img11,
    img12
];

const nextTabReady = ref(false);
const tabs = ref([]);
const tabElems = ref([]);
const modalListenersAdded = ref(false);

const filterOptions = {
	'medications' : [
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'conditions', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		],
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'therapies', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		],
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'categories', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		],
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'side_effects', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'frequency', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Severity', 'value': 'severity', 'dir': 'desc' }
				], forSort: true
			}
		]
	],
	'categories': [
		[
			{label: "Has been Surveyed", value: 'true', type: 'selection', filterOn: 'has_been_surveyed', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		]
	],
	'conditions': [
		[
			{label: "Has been Surveyed", value: 'true', type: 'selection', filterOn: 'has_been_surveyed', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		],
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'symptoms', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'frequency', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Severity', 'value': 'severity', 'dir': 'desc' }
				], forSort: true
			}
		]
	],
	'symptoms': [
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'conditions', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'frequency', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Severity', 'value': 'severity', 'dir': 'desc' }
				], forSort: true
			}
		]
	],
	'therapies': [
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'medications', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'usage_count', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Rating', 'value': 'rating', 'dir': 'desc' }
				], forSort: true
			}
		]
	],
	'side_effects': [
		[
			// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'medications', selected: null},
			{
				label: 'Popularity', type: 'popup', options: [
					{ 'label': 'Popularity', 'value': 'frequency', 'dir': 'desc' },
					{ 'label': 'Name', 'value': 'name', 'dir': 'asc' },
					{ 'label': 'Severity', 'value': 'severity', 'dir': 'desc' }
				], forSort: true
			}
		]
	]
}

const keywordError = ref(false);
const loadingMainContent = ref(false);
const mainContent = ref(null);
const stats = ref([]);
const typeForDemographicStat = ref(null);

const tabsData = computed(() => {
	switch(props.basic) {
		case 'medications':
			typeForDemographicStat.value = 'Medication';
			return [{ slug: 'conditions', name: "Conditions", url: 'medications/related/conditions/' + props.specific, parent: 'conditions', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'usage_count', dir: 'desc' } },
				{ slug: 'therapies', name: "Therapies", url: 'medications/related/therapies/' + props.specific, parent: 'therapies', filterOptions: filterOptions[props.basic][1], selectedFilter: { sort: 'usage_count', dir: 'desc' } },
				{ slug: 'categories', name: "Categories", url: 'medications/related/categories/' + props.specific, parent: 'categories', filterOptions: filterOptions[props.basic][2], selectedFilter: { sort: 'usage_count', dir: 'desc' } },
				{ slug: 'side_effects', name: "Side Effects", url: 'medications/related/side_effects/' + props.specific, parent: 'side_effects', filterOptions: filterOptions[props.basic][3], selectedFilter: { sort: 'frequency', dir: 'desc' } }];
		case 'categories':
			typeForDemographicStat.value = 'Category';
			return [
				{ slug: 'medications', name: "Medications", url: 'categories/related/medications/' + props.specific, parent: 'medications', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'usage_count', dir: 'desc' } }
			];
		case 'conditions':
			typeForDemographicStat.value = 'Condition';
			return [
				{ slug: 'medications', name: "Medications", url: 'conditions/related/medications/' + props.specific, parent: 'medications', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'usage_count', dir: 'desc' } },
				{ slug: 'symptoms', name: "Symptoms", url: 'conditions/related/symptoms/' + props.specific, parent: 'symptoms', filterOptions: filterOptions[props.basic][1], selectedFilter: { sort: 'frequency', dir: 'desc' } }
			];
		case 'symptoms':
			typeForDemographicStat.value = 'Symptom';
			return [
				{ slug: 'conditions', name: "Conditions", url: 'symptoms/related/conditions/' + props.specific, parent: 'conditions', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'frequency', dir: 'desc' } },
			];
		case 'therapies':
			typeForDemographicStat.value = 'Therapy';
			return [
				{ slug: 'medications', name: "Medications", url: 'therapies/related/medications/' + props.specific, parent: 'medications', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'usage_count', dir: 'desc' } }
			];
		case 'side_effects':
			typeForDemographicStat.value = 'Side Effect';
			return [
				{ slug: 'medications', name: "Medications", url: 'side_effects/related/medications/' + props.specific, parent: 'medications', filterOptions: filterOptions[props.basic][0], selectedFilter: { sort: 'frequency', dir: 'desc' } },
			];
		default:
			return [];
	}
});

const activeIndex = ref(0);
const currentTab = ref(tabsData.value.length > 0 ? tabsData.value[0].slug : '');
const changeTab = (slug) => {
	if(!slug) {
		currentTab.value = tabsData.value.length > 0 ? tabsData.value[0].slug : '';
		return;
	}

	const queryParams = new URLSearchParams(window.location.search);
	if(!tabsData.value.find(tab => tab.slug === slug)) {
		currentTab.value = tabsData.value.length > 0 ? tabsData.value[0].slug : '';
		queryParams.delete('tab');
	}
	else{
		currentTab.value = slug;
		queryParams.set('tab', slug);
	}
	// window.history.replaceState({}, '', window.location.pathname + '?' + queryParams.toString());
	router.replace({path: route.path, query: { ...route.query, ...Object.fromEntries(queryParams) },});
	activeIndex.value = tabsData.value.findIndex(t => t.slug === currentTab.value);
}

const fetchData = async () => {
	if(loadingMainContent.value) return;
    loadingMainContent.value = true;
    let url = props.basic + '/' + props.specific;
    try {
		stats.value = [];
		mainContent.value = null;
        const content = await apiClient.get(url);
        if (content.data.data) {
            mainContent.value = content.data.data;
            pageSettings.value.title = mainContent.value.name;
        }
		if(content.data.data.stats){
			stats.value = content.data.data.stats;
		}

        tabs.value = tabsData.value;
    }
    catch (error) {
		if(error.status === 404){
			keywordError.value = true;
			pageSettings.value.title = 'Page Not Found';
			mainContent.value = null;
			tabs.value = [];
		}
		else{
			keywordError.value = false;
		}
        let errorObj = await useHttpErrorManager().handleError(error, false);
    }
	finally {
	    loadingMainContent.value = false;
    }
}

const setDefaults = () => {
	switch(props.basic) {
		case 'medications':
			pageSettings.value.basePage = 'Medication';
			break;
		case 'categories':
			pageSettings.value.basePage = 'Category';
			break;
		case 'conditions':
			pageSettings.value.basePage = 'Condition';
			break;
		case 'symptoms':
			pageSettings.value.basePage = 'Symptom';
			break;
		case 'therapies':
			pageSettings.value.basePage = 'Therapy';
			break;
		case 'side_effects':
			pageSettings.value.basePage = 'Side Effect';
			break;
		default:
			pageSettings.value.basePage = '-';
			break;
	}
}

const selectRandomImage = () => {
	const randomIndex = Math.floor(Math.random() * images.length);
	specificBg.value = images[randomIndex];
}

const preventScrollKeys = (e) => {
    if ([32, 33, 34, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
        e.preventDefault();
    }
}

const preventScroll = (e) => {
    e.preventDefault();
}

const preventMiddleClickScroll = (e) => {
    if (e.button === 1) {
        e.preventDefault();
    }
}

const enableScroll = () => {
    setTimeout(() => {
        document.body.classList.remove('force-show-scroll');

        window.removeEventListener('scroll', preventScroll);
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventScroll);
        window.removeEventListener('keydown', preventScrollKeys);
        window.removeEventListener('mousedown', preventMiddleClickScroll);
    }, 300);
}

const disableScroll = () => {
    document.body.classList.add('force-show-scroll');
    window.addEventListener('scroll', preventScroll, { passive: false });
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    window.addEventListener('keydown', preventScrollKeys, { passive: false });
    window.addEventListener('mousedown', preventMiddleClickScroll, { passive: false });
}

const hideTabEvent = (event) => {
	if (!nextTabReady.value) {
		event.preventDefault();
		const currTabBtn = event.target;
		const currentTabId = currTabBtn.dataset.bsTarget
		const currentTab = document.querySelector(currentTabId)
		const currentTabItemsContainer = currentTab.querySelector(".tab-items");

		//Show loading indicator and set opacity for fade effect
		helper.loaderOn(currentTabItemsContainer);

		setTimeout(() => {
			helper.loaderOff(currentTabItemsContainer);
			nextTabReady.value = true;
			const nextTabBtn = event.relatedTarget;
			const nextTab = tab.getOrCreateInstance(nextTabBtn);
			nextTab.show();
		}, 1000);
	} else {
		nextTabReady.value = false;
	}
}

const setInitialVh = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--specific-sm-vh', `${vh * 100}px`);
};

const statToLoad = ref(null);
const openStatsModal = (stat) => {
	if(!stat.code && !stat.extra) return;
	statToLoad.value = stat;
	showStatModal.value = true;
}
const handleModalClose = (ref) => {
	if (ref === 'stat')
		showStatModal.value = false;
	else
		showInfoModal.value = false;

	enableScroll();
}

const initializePage = async () => {
	selectRandomImage();
	setDefaults();
	await fetchData();

	tabElems.value.forEach((tabEl) => {
		tabEl.removeEventListener('hide.bs.tab', hideTabEvent)
	});

	tabElems.value = document.querySelectorAll('#mainTabsContainer button[data-bs-toggle="tab"]');
	tabElems.value.forEach((tabEl) => {
		tabEl.addEventListener('hide.bs.tab', hideTabEvent)
	});

	if(!modalListenersAdded.value) {
		document.removeEventListener('show.bs.modal', disableScroll);
		document.removeEventListener('hide.bs.modal', enableScroll);
		modalListenersAdded.value = true;
	}

	setInitialVh();

	await helper.visitTracker('medications', props.specific, router.currentRoute.value.path);
}

watch(() => props.specific, async (newSpecificKeyword) => {
	if(newSpecificKeyword) {
		await initializePage();
	}
});

watch(() => props.basic, async (newBasicKeyword) => {
	if(newBasicKeyword) {
		pageSettings.value.previousPage = '/' + newBasicKeyword;
		keywordError.value = !['medications', 'categories', 'conditions', 'symptoms', 'therapies', 'side_effects'].includes(newBasicKeyword);
		if(!keywordError.value) {
			await initializePage();
		}
	}
}, {	immediate: true});

onMounted(() => {
	const queryParams = new URLSearchParams(window.location.search);
	const tab = queryParams.get('tab');
	if (tab) {
		changeTab(tab);
	}
})

onUnmounted(() => {
    document.removeEventListener('show.bs.modal', disableScroll);
    document.removeEventListener('hide.bs.modal', enableScroll);
})

</script>